import { t, Trans } from '@lingui/macro'
import { Edit, Mail, Save } from '@mui/icons-material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid, IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { checkContactUpdateRequest, createCaseByFlow } from 'app/services/sfAuth/sfData/sfCase'
import { mapContact, saveContactParsed } from 'app/services/sfAuth/sfData/sfContact'
import { ActionsColumnHeader } from 'app/views/common-components/ActionsColumnHeader'
import CustomDataTable from 'app/views/common-components/CustomDataTable'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { parseExtensionPhoneToSF } from 'app/views/common/Formats'
import { FormErrorLabel } from 'app/views/common/labels/FormErrorLabel'
import { emailTrans, requiredTrans } from 'app/views/forms/formTranslations'
import { MUEditedByLabel } from 'app/views/forms/multiuser/components/MUEditedByLabel'
import FindContactButton from 'app/views/grants/FindContactButton'
import { muiTextLabels } from 'app/views/utilities/muiDataTablesTranslations'
import * as crypto from 'crypto'
import { Formik, useField, useFormikContext } from 'formik'
import { isEqual } from 'lodash'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { languages, myI18n } from 'translation/I18nConnectedProvider'
import * as Yup from 'yup'
import { getMuState, parseFormLabelText } from '../../../common/Common'
import {
  endEditingField,
  startEditingField
} from '../../../multiuser/grpcMultiuserEdit'

export const validateContactsRolesAndInfo = ({
  array,
  createError,
  roles,
  path,
  contactsRequestMap = {}
}) => {
  if (!array) {
    return true
  }
  const countMap = {}
  let missingInfoError = false
  array.forEach(obj => {
    const contactId = obj.actions.contactInfo.id
    if (!obj.deleted) {
      const { jobTitle, phoneNumber, workPhone, mobilePhone } = obj
      if (!countMap[obj.role]) {
        countMap[obj.role] = 1
      } else {
        countMap[obj.role] = countMap[obj.role] + 1
      }
      const missingInfo =
        !jobTitle || (!phoneNumber && !workPhone && !mobilePhone)
      if (missingInfo && !contactsRequestMap[contactId]) {
        missingInfoError = true
      }
    }
  })
  const notEnoughRolesError =
    roles &&
    roles.some(role => {
      const min = +role.min
      if (!min) {
        return false
      }
      return countMap[role.apiValue] < min || !countMap[role.apiValue]
    })

  if (notEnoughRolesError) {
    return createError({
      path,
      message: (
        <Trans>
          All roles must have assigned the minimum number of contacts
        </Trans>
      )
    })
  } else if (missingInfoError) {
    return createError({
      path,
      message: (
        <Trans>
          Some of affiliated contacts have required information missing
        </Trans>
      )
    })
  }
  return true
}

Yup.addMethod(Yup.array, 'checkAssociatedContacts', function (item, formikRef) {
  return this.test('checkAssociatedContacts', null, function (array) {
    const { path, createError } = this
    const { roles } = item.typeProps
    const values = formikRef?.current?.values ?? {}
    const contactsRequestMap = values?.other?.tempValues?.[item.id]?.contactsRequestMap || {}

    return validateContactsRolesAndInfo({
      array,
      createError,
      path,
      roles,
      contactsRequestMap
    })
  })
})

export const formAssociatedContactsListValidation = (item, options = {}) => {
  const { formikRef } = options

  return Yup.array().checkAssociatedContacts(item, formikRef)
}

export const formProjectAffiliationsExtractSaveKey = ({
  sfObject,
  saveMap,
  value,
  connectedProps,
  item
}) => {
  const { typeProps } = item
  const { connectedObject } = connectedProps
  if (typeProps.roles) {
    typeProps.roles.forEach(role => {
      let { sfField, apiValue } = role
      if (!sfField) {
        // If form was not saved and updated with new functionality we need to determine the field to save
        switch (role.apiValue) {
          case 'First Signer':
            sfField = 'aff_grantee_first_signer__c'
            break
          case 'Second Signer':
            sfField = 'aff_grantee_second_signer__c'
            break
          case 'Primary Contact':
            sfField = 'aff_grantee_primary_contact__c'
            break
          case 'Secondary Contact':
            sfField = 'Secondary_Contact__c'
            break
          case 'Accounting Contact':
            sfField = 'Accounting_Contact__c'
            break
          default:
            break
        }
      }
      const existingValue = value.find(obj => obj.role === apiValue && !obj.deleted && obj.contactId)
      if (existingValue) {
        if (sfField) {
          saveMap[connectedObject][sfField] = existingValue.contactId
        }
      } else if (sfField) {
        saveMap[connectedObject][sfField] = null
      }
    })
  }
}

export const formProjectAffiliaitonsDefaultValue = (
  obj,
  additionalInfo,
  item
) => {
  const { roles = [] } = item.typeProps
  if (!obj || !additionalInfo.opportunityAffiliatedContacts) {
    return []
  } else {
    const toMap = additionalInfo.opportunityAffiliatedContacts || []
    const avaliableRoles = roles.map(role => role.apiValue)
    return toMap
      .filter(
        obj =>
          avaliableRoles.includes(obj.Role__c) && obj.Status__c !== 'Former'
      )
      .map(item => {
        const contactInfo = mapContact(item.Contact__c)
        return {
          ...contactInfo,
          contactId: contactInfo.id,
          id: item.Id,
          role: item.Role__c,
          relationship: item.Relationship__c,
          actions: {
            id: item.Id,
            contactInfo
          }
        }
      })
  }
}

export const formProjectAffiliaitonsValueToText = (v = [], question) => {
  const { roles } = question.typeProps
  const toRet = langVersion => {
    const roleToTrans = {}
    roles.forEach(role => {
      if (role.apiValue) {
        roleToTrans[role.apiValue] = parseFormLabelText({
          text: role.label,
          langVersion
        })
      }
    })
    return (
      <div>
        <Grid container style={{ width: '100%' }}>
          <Grid item xs style={{ padding: 4 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Contact name</Trans>
            </Typography>
          </Grid>
          <Grid item xs style={{ padding: 4 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Contact e-mai</Trans>
            </Typography>
          </Grid>
          <Grid item xs style={{ padding: 4 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Contact role</Trans>
            </Typography>
          </Grid>
        </Grid>
        {v
          .filter(c => !c.deleted)
          .map((contact, index) => {
            return (
              <>
                <Divider />
                <Grid container key={index} style={{ width: '100%' }}>
                  <Grid item xs style={{ padding: 4 }}>
                    <Typography>{contact.name}</Typography>
                  </Grid>
                  <Grid item xs style={{ padding: 4 }}>
                    <Typography>{contact.email}</Typography>
                  </Grid>
                  <Grid item xs style={{ padding: 4 }}>
                    <Typography>
                      {roleToTrans[contact.role] || contact.role}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )
          })}
      </div>
    )
  }

  const retObj = languages.reduce((acc, lang) => {
    acc[lang] = toRet(lang)
    return acc
  }, {})

  return retObj
}

/**
 * Form element which allows user to select contacts from organizations address book as references in the project. Renders a Material UI table, displaying all currently added affiliations.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {SalesforceObjectConnection}  connectedObject Connected Salesforce object of type Opportunity.
 * @param  {object[]}  typeProps.roles Roles, configured in editor, for which user must assign contacts from the address book. Each role has a minimum and maximum number of references that can be assigned to it.
 * @param  {boolean}  [typeProps.showRelationshipColumn=false] If table should display additional column containing relationship of the contact
 */
export const FormProjectAffiliations = ({
  typeProps = {},
  title,
  value = [],
  disabled,
  saveDisabled,
  connectedObject,
  id,
  muBag,
  useMultiuser,
  editMode,
  reloadLastModifiedDates,
  langVersion
}) => {
  const invalid = Boolean(
    !connectedObject ||
      !connectedObject.Id ||
      connectedObject.attributes.type !== 'Opportunity'
  )
  const { roles = [], showRelationshipColumn } = typeProps
  const { enqueueSnackbar } = useSnackbar()
  const { values, setFieldValue, setFieldTouched } = useFormikContext()
  const [field, meta] = useField(id)
  const { touched } = meta
  const { isEdited, userColor, userName } = getMuState({ values, id })
  const queryClient = useQueryClient()
  const organization = useSelector(state => state.organization)
  const organizationId = organization.id
  const contactsRequestMap = values?.other?.tempValues?.[id]?.contactsRequestMap

  const contactResults = useQueries({
    queries: value.map(c => ({
      queryKey: ['contactRequest', c.contactId, organizationId],
      queryFn: () => checkContactUpdateRequest(c.contactId, organizationId),
      staleTime: 5 * 60 * 1000
    }))
  })

  const hasActiveCaseRequestMap = useMemo(() =>
    value.reduce((map, contact, index) => {
      map[contact.contactId] = contactResults[index]?.data ?? false
      return map
    }, {}),
  [value, contactResults, organizationId]
  )

  useEffect(() => {
    // const hasActiveCaseRequestMap =
    //   value.reduce((map, contact, index) => {
    //     map[contact.contactId] = contactResults[index]?.data ?? false
    //     return map
    //   }, {})
    if (
      !isEqual(contactsRequestMap, hasActiveCaseRequestMap) &&
      Object.keys(hasActiveCaseRequestMap).length > 0
    ) {
      setFieldValue(
        `other.tempValues.${id}.contactsRequestMap`,
        hasActiveCaseRequestMap
      )
    }
  }, [value, contactResults, organizationId])

  if (invalid && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        <Trans>
          There is no object connected in editor or connected object is not of
          "Opportunity" type!
        </Trans>
      </div>
    )
  }

  const rolesLeft = []
  const valueToLabel = {}
  roles.forEach(role => {
    let usedCount = 0
    const label = role.label?.[langVersion]
    valueToLabel[role.apiValue] = label
    value.forEach(obj => {
      if (obj.role === role.apiValue && !obj.deleted) {
        usedCount++
      }
    })
    rolesLeft.push({
      label,
      askForRelationship: role.askForRelationship,
      value: role.apiValue,
      defaultOrganization: role.defaultOrganization,
      error: role.min && usedCount < +role.min,
      min: role.min || 0,
      current: usedCount,
      limit: role.limit || +role.min + 1
    })
  })

  const onContactFound = async contact => {
    const toSet = [...value]
    if (
      toSet.some(
        obj =>
          obj.role === contact.Role__c &&
          obj.contactId === contact.Id &&
          !obj.deleted
      )
    ) {
      enqueueSnackbar(
        <Trans>This contact is already associated with this role!</Trans>,
        { variant: 'error' }
      )
    } else {
      const fakeId = 'fake' + crypto.randomBytes(8).toString('hex')
      const contactInfo = mapContact(contact)

      await queryClient.fetchQuery(
        ['contactRequest', contact.Id, organizationId],
        () => checkContactUpdateRequest(contact.Id, organizationId)
      )

      toSet.push({
        ...contactInfo,
        id: fakeId,
        contactId: contactInfo.id,
        actions: {
          id: fakeId,
          contactInfo
        }
      })
      setFieldValue(id, toSet)
      if (useMultiuser) {
        endEditingField({
          ...muBag,
          fieldId: id,
          fieldValue: toSet,
          lockId: values.muInfo.lockId
        })
      }
    }
  }

  const onContactUpdate = newValues => {
    const toSet = [...value]
    toSet.forEach((aff, index) => {
      const bool = aff.contactId === newValues.id
      if (bool) {
        toSet[index] = {
          ...aff,
          ...newValues,
          id: aff.id,
          role: aff.role,
          relationship: aff.relationship,
          contactId: newValues.id,
          actions: {
            id: aff.id,
            contactInfo: { ...aff.actions.contactInfo, ...newValues }
          }
        }
      }
      return bool
    })
    setFieldValue(id, toSet)
    if (useMultiuser) {
      endEditingField({
        ...muBag,
        fieldId: id,
        fieldValue: toSet,
        lockId: values.muInfo.lockId
      })
    }
  }

  return (
    <>
      <CustomDataTable
        data={value.filter(v => !v.deleted)}
        title={
          <Grid container wrap='nowrap' style={{ marginLeft: '32px' }}>
            <FindContactButton
              preview={saveDisabled}
              onDialogClose={e => {
                if (useMultiuser) {
                  endEditingField({
                    ...muBag,
                    fieldId: id,
                    fieldValue: field.value,
                    lockId: values.muInfo.lockId
                  })
                }
              }}
              onDialogOpen={e => {
                if (useMultiuser) {
                  startEditingField({
                    ...muBag,
                    fieldId: id
                  })
                }
              }}
              allowCreatingNewContact
              disabled={
                disabled || editMode || rolesLeft.length === 0 || saveDisabled
              }
              onSelect={onContactFound}
              roles={rolesLeft}
            />
          </Grid>
        }
        columns={[
          {
            name: 'name',
            label: myI18n._(t`Contact name`),
            options: {
              customHeadLabelRender: props => {
                return (
                  <div>
                    <Trans>Contact name</Trans>
                  </div>
                )
              },
              customBodyRender: (value, tableMeta, updateValue) => {
                return <div style={{ flex: 1 }}>{value}</div>
              }
            }
          },
          {
            name: 'email',
            label: myI18n._(t`Contact e-mail`),
            options: {
              customHeadLabelRender: props => <Trans>E-mail</Trans>
            }
          },
          {
            name: 'role',
            label: myI18n._(t`Contact role`),
            options: {
              customHeadLabelRender: props => <Trans>Contact role</Trans>,
              customBodyRender: value => valueToLabel[value]
            }
          },
          {
            name: 'relationship',
            label: myI18n._(t`Relationship`),
            options: {
              display: showRelationshipColumn ? 'true' : 'excluded',
              customHeadLabelRender: props => <Trans>Relationship</Trans>
            }
          },
          {
            name: 'actions',
            label: myI18n._(t`Actions`),
            options: {
              sort: false,
              customHeadLabelRender: props =>
                <ActionsColumnHeader />,
              customBodyRender: (value, tableMeta, updateValue) => {
                const { contactInfo } = value
                return (
                  <Grid
                    container wrap='nowrap' sx={{
                      justifyContent: {
                        xs: 'flex-start',
                        lg: 'flex-start'
                      }
                    }}
                  >
                    <EditContactPanel
                      disabled={disabled || saveDisabled}
                      onContactUpdate={onContactUpdate}
                      contactInfo={contactInfo}
                      opportunityId={connectedObject.Id}
                      reloadLastModifiedDates={reloadLastModifiedDates}
                      programManager={
                        connectedObject.Assigned_Program_Manager__c
                      }
                      hasActiveCaseRequest={hasActiveCaseRequestMap[contactInfo.id]}
                      setHasActiveCaseRequestCallback={contactId => {
                        queryClient.setQueryData(
                          ['contactRequest', contactId, organizationId],
                          true
                        )
                        queryClient.invalidateQueries(
                          { queryKey: ['contactRequest', contactId, organizationId] }
                        )
                      }}
                    />

                    <IconButton
                      disabled={disabled || saveDisabled}
                      onClick={() => {
                        const toSet = [...field.value]
                        toSet.some((obj, index) => {
                          const bool = obj.id === value.id
                          if (bool) {
                            const item = { ...obj }
                            item.deleted = true
                            toSet[index] = item
                          }
                          return bool
                        })
                        setFieldValue(id, toSet)
                        if (useMultiuser) {
                          endEditingField({
                            ...muBag,
                            fieldId: id,
                            fieldValue: toSet,
                            lockId: values.muInfo.lockId
                          })
                        }
                        if (!touched) {
                          setFieldTouched(id)
                        }
                      }}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </Grid>
                )
              }
            }
          }
        ]}
        options={{
          textLabels: muiTextLabels(myI18n),
          search: false,
          filter: false,
          selectableRows: 'none',
          print: false,
          download: false,
          viewColumns: false
        }}
      />
      <FormErrorLabel error={meta.error} id={id} />

      {isEdited && (
        <div style={{ marginTop: 5 }}>
          <MUEditedByLabel color={userColor} userName={userName} />
        </div>
      )}
    </>
  )
}

const phoneValidation = Yup.string()
  .nullable()
  .test(
    'isValidPhone',
    () => <Trans>Phone Number must have at least 10 digits</Trans>,
    value => {
      if (value) {
        const filtered = value.replace(/[^0-9]/g, '')
        return filtered.length >= 10
      } else {
        return true
      }
    }
  )

export const EditContactPanel = ({
  reloadLastModifiedDates,
  disabled,
  contactInfo,
  onContactUpdate,
  opportunityId,
  renderIconOnLeft,
  programManager,
  setHasActiveCaseRequestCallback,
  hasActiveCaseRequest
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [updating, setUpdating] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const user = useSelector(state => state.user)
  const organization = useSelector(state => state.organization)
  const { phoneNumber, mobilePhone, jobTitle, workPhone, owner } = contactInfo
  const missingInfo = (
    !jobTitle ||
    (!phoneNumber && !workPhone && !mobilePhone)
  ) && !hasActiveCaseRequest
  const phoneMissing = !phoneNumber && !workPhone && !mobilePhone
  const isContactOwner = owner === user.userId

  const initialValues = { ...contactInfo }
  const countryCodesKeys = [
    'phoneNumberCountryCode',
    'mobilePhoneCountryCode',
    'workPhoneCountryCode'
  ]
  countryCodesKeys.forEach(key => {
    if (!initialValues[key]) {
      delete initialValues[key]
    }
  })

  return (
    <Formik
      initialValues={{
        phoneNumberCountryCode: '1',
        mobilePhoneCountryCode: '1',
        workPhoneCountryCode: '1',
        ...initialValues
      }}
      enableReinitialize
      validateOnBlur
      validateOnMount
      validationSchema={Yup.object().shape({
        firstName: Yup.string().nullable().required(requiredTrans),
        lastName: Yup.string().nullable().required(requiredTrans),
        jobTitle: Yup.string().nullable().required(requiredTrans),
        providedOrganizationName: Yup.string()
          .nullable()
          .required(requiredTrans),
        email: Yup.string()
          .nullable()
          .ensure()
          .required(requiredTrans)
          .email(() => emailTrans),
        phoneNumber: phoneValidation,
        mobilePhone: phoneValidation,
        workPhone: phoneValidation
      })}
    >
      {({ values, dirty, isValid }) => {
        const { phoneNumber, mobilePhone, workPhone } = values
        const noPhoneProvided = !phoneNumber && !mobilePhone && !workPhone

        const alert = (
          <Alert severity='warning'>
            <Trans>
              Some of required information in this contact are missing
            </Trans>
          </Alert>
        )
        const iconButton = (
          <Tooltip title={
            (!isContactOwner && hasActiveCaseRequest)
              ? <Trans>EDIT_AFFILIATED_CONTACT_CURRENT_INFORMATION_CHANGE_REQUEST_TOOLTIP</Trans>
              : <Trans>Update affiliated contact</Trans>
            }
          >
            <span>
              <IconButton
                disabled={disabled || (!isContactOwner && hasActiveCaseRequest)}
                onClick={e => {
                  setDialogOpen(true)
                }}
              >
                <Edit />
              </IconButton>
            </span>
          </Tooltip>
        )

        return (
          <>
            <Dialog open={dialogOpen} fullWidth maxWidth='md'>
              <DialogTitleWithIconClose
                handleClose={e => {
                  setDialogOpen(false)
                }}
                label={<Trans>Contact information</Trans>}
              />
              {!isContactOwner && (
                <div style={{ paddingLeft: 24, paddingRight: 24 }}>
                  <Alert severity='info'>
                    <Trans>
                      You are not the owner of this contact, your change request
                      will be reviewed
                    </Trans>
                  </Alert>
                </div>
              )}

              <DialogContent>
                <Grid container direction='column' gap='16px'>
                  <Grid
                    container
                    wrap='nowrap'
                  >
                    <Grid xs item style={{ paddingRight: 5 }}>
                      <FormTopLabel required>
                        <Trans>First Name</Trans>
                      </FormTopLabel>
                      <TextFieldWithFormik
                        disabled={updating}
                        variant='outlined'
                        required
                        name='firstName'
                        fullWidth
                      />
                    </Grid>
                    <Grid xs item style={{ paddingLeft: 5 }}>
                      <FormTopLabel required>
                        <Trans>Last Name</Trans>
                      </FormTopLabel>
                      <TextFieldWithFormik
                        disabled={updating}
                        variant='outlined'
                        name='lastName'
                        fullWidth
                        required
                      />
                    </Grid>
                  </Grid>

                  <Grid item>
                    <FormTopLabel required>
                      <Trans>E-mail</Trans>
                    </FormTopLabel>
                    <TextFieldWithFormik
                      disabled={updating}
                      variant='outlined'
                      required
                      type='email'
                      name='email'
                      fullWidth
                    />
                  </Grid>

                  <Grid item>
                    <FormTopLabel required>
                      <Trans>Phone number</Trans>
                    </FormTopLabel>
                    <Grid container>

                      <Grid item>
                        <TextFieldWithFormik
                          disabled={updating}
                          variant='outlined'
                          name='phoneNumberCountryCode'
                          type='countryCode'
                        />
                      </Grid>

                      <Grid item xs>
                        <TextFieldWithFormik
                          disabled={updating}
                          variant='outlined'
                          type='phone'
                          name='phoneNumber'
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* {phoneMissing && (
                  <>
                    <Grid container style={{ marginBottom: 10 }}>
                      <Grid item>
                        <TextFieldWithFormik
                          disabled={updating}
                          variant='outlined'
                          name='mobilePhoneCountryCode'
                          InputProps={{
                            inputComponent: PhoneCountryCodeFormat
                          }}
                          inputProps={{
                            maxLength: 5
                          }}
                          style={{ width: 65 }}
                        />
                      </Grid>

                      <Grid item xs>
                        <TextFieldWithFormik
                          disabled={updating}
                          variant='outlined'
                          label={<Trans>Mobile Phone</Trans>}
                          helperText={
                            <Trans>At least one phone number is required</Trans>
                          }
                          name='mobilePhone'
                          InputProps={{
                            inputComponent: PhoneFormatWithExtension
                          }}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Grid container style={{ marginBottom: 10 }}>
                      <Grid item>
                        <TextFieldWithFormik
                          disabled={updating}
                          variant='outlined'
                          name='workPhoneCountryCode'
                          InputProps={{
                            inputComponent: PhoneCountryCodeFormat
                          }}
                          inputProps={{
                            maxLength: 5
                          }}
                          style={{ width: 65 }}
                        />
                      </Grid>

                      <Grid item xs>
                        <TextFieldWithFormik
                          disabled={updating}
                          variant='outlined'
                          label={<Trans>Work Phone</Trans>}
                          helperText={
                            <Trans>At least one phone number is required</Trans>
                          }
                          name='workPhone'
                          InputProps={{
                            inputComponent: PhoneFormatWithExtension
                          }}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </>
                )} */}

                  <Grid item>
                    <FormTopLabel required>
                      <Trans>Organization</Trans>
                    </FormTopLabel>
                    <TextFieldWithFormik
                      disabled={updating}
                      variant='outlined'
                      name='providedOrganizationName'
                      fullWidth
                    />
                  </Grid>

                  <Grid item>
                    <FormTopLabel required>
                      <Trans>Job title</Trans>
                    </FormTopLabel>
                    <TextFieldWithFormik
                      disabled={updating}
                      variant='outlined'
                      name='jobTitle'
                      fullWidth
                    />
                  </Grid>

                </Grid>

              </DialogContent>
              <DialogActions>
                {isContactOwner
                  ? (
                    <Button
                      variant='contained'
                      color='primary'
                      disabled={!isValid || updating || noPhoneProvided}
                      onClick={e => {
                        setUpdating(true)
                        saveContactParsed(values).then(
                          result => {
                            reloadLastModifiedDates().then(result => {
                              enqueueSnackbar(<Trans>Contact updated!</Trans>, {
                                variant: 'info'
                              })
                              setUpdating(false)
                              const toForward = { ...values }
                              toForward.name =
                              values.firstName + ' ' + values.lastName
                              onContactUpdate(toForward)
                              setDialogOpen(false)
                            })
                          },
                          reject => {
                            enqueueSnackbar(
                              <Trans>
                                Error ocurred while updating contact!
                              </Trans>,
                              {
                                variant: 'error'
                              }
                            )
                            setUpdating(false)
                          }
                        )
                      }}
                    >
                      <Save style={{ marginRight: 5 }} />
                      <Trans>Update</Trans>
                    </Button>
                    )
                  : (
                    <Button
                      variant='contained'
                      color='primary'
                      disabled={!isValid || updating || noPhoneProvided || !dirty}
                      onClick={e => {
                        setUpdating(true)
                        let body = ''
                        const textData = [
                          { key: 'firstName', text: myI18n._(t`First name`) },
                          { key: 'lastName', text: myI18n._(t`Last name`) },
                          { key: 'email', text: myI18n._(t`Email`) },
                          {
                            key: 'providedOrganizationName',
                            text: myI18n._(t`Organization`)
                          },
                          { key: 'jobTitle', text: myI18n._(t`Job title`) }
                        ]
                        textData.forEach(obj => {
                          const { key, text } = obj
                          if (values[key] !== initialValues[key]) {
                            body += text + ': ' + values[key] + '\n'
                          }
                        })
                        if (
                          values.phoneNumber !== initialValues.phoneNumber ||
                        values.phoneNumberCountryCode !==
                          initialValues.phoneNumberCountryCode
                        ) {
                          body +=
                          myI18n._(t`Phone number`) +
                          ': ' +
                          parseExtensionPhoneToSF(
                            values.phoneNumber,
                            values.phoneNumberCountryCode
                          )
                        }
                        const toCreate = {
                          title: 'Update contact information',
                          type: 'Request contact information update',
                          contact: values.id,
                          language: user.language,
                          opportunityId,
                          userName: user.displayName,
                          organization: organization.id,
                          description: body
                        }
                        if (programManager) {
                          toCreate.OwnerId = programManager
                          toCreate.Skip_Assignment__c = true
                        }
                        createCaseByFlow(toCreate).then(
                          result => {
                            reloadLastModifiedDates().then(result => {
                              enqueueSnackbar(
                                <Trans>Information submitted!</Trans>,
                                {
                                  variant: 'info'
                                }
                              )
                              setUpdating(false)
                              setDialogOpen(false)
                              setHasActiveCaseRequestCallback?.(values.id)
                            })
                          },
                          reject => {
                            enqueueSnackbar(
                              <Trans>Error Submitting Case</Trans>,
                              {
                                variant: 'error'
                              }
                            )
                            setUpdating(false)
                          }
                        )
                      }}
                    >
                      <Mail style={{ marginRight: 5 }} />
                      <Trans>Submit changes</Trans>
                    </Button>
                    )}
              </DialogActions>
            </Dialog>
            {renderIconOnLeft
              ? (
                <>
                  <Grid item>{iconButton}</Grid>
                  {missingInfo && <Grid item>{alert}</Grid>}
                </>
                )
              : (
                <Grid
                  container wrap='nowrap' sx={{
                    justifyContent: {
                      xs: 'flex-start',
                      lg: 'flex-start'
                    }
                  }} alignItems='center'
                >
                  {missingInfo && alert}
                  {iconButton}
                </Grid>
                )}
          </>
        )
      }}
    </Formik>
  )
}
